const SITE_ORIGIN = 'https://trinitydentalarts.com';

// ─────────────────────────────────────────────
// CANONICAL @id REFERENCES
// All @id values point to the canonical domain regardless of serving hostname.
// ─────────────────────────────────────────────
const ORG_ID  = `${SITE_ORIGIN}/#organization`;
const DOC_ID  = `${SITE_ORIGIN}/#dr-gjelaj`;
const SITE_ID = `${SITE_ORIGIN}/#website`;

// Lightweight reference objects — pages link to the entities defined in index.html @graph
const ORG_REF  = { '@id': ORG_ID };
const DOC_REF  = { '@id': DOC_ID };

// Legacy performer reference kept for backward compat with existing page schemas
const PERFORMER = { '@id': ORG_ID };

const DR_GJELAJ_PERSON = {
  '@type': 'Person',
  '@id': DOC_ID,
  name: 'Dr. Xhoana Gjelaj',
  jobTitle: 'Doctor of Dental Medicine, Board-Certified Implantologist',
  honorificPrefix: 'Dr.',
  honorificSuffix: 'DMD, MAGD, DABOI/ID, DICOI, FAAID',
  worksFor: ORG_REF,
  url: `${SITE_ORIGIN}/meet-the-doctor`,
};

// ─────────────────────────────────────────────
// DENTIST / LOCAL BUSINESS — references the index.html @graph entity
// ─────────────────────────────────────────────
export const dentistSchema = {
  '@context': 'https://schema.org',
  '@type': ['Dentist', 'LocalBusiness', 'MedicalBusiness'],
  '@id': ORG_ID,
};

// ─────────────────────────────────────────────
// DOCTOR / PERSON SCHEMA — references the index.html @graph entity
// ─────────────────────────────────────────────
export const drGjelajSchema = {
  '@context': 'https://schema.org',
  ...DR_GJELAJ_PERSON,
};

// ─────────────────────────────────────────────
// DENTAL IMPLANTS
// ─────────────────────────────────────────────
export const dentalImplantsProcedureSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/dental-implants#procedure`,
  name: 'Dental Implants',
  alternateName: ['Tooth Implant', 'Implant Dentistry', 'Endosseous Implant'],
  url: `${SITE_ORIGIN}/dental-implants`,
  procedureType: 'https://schema.org/SurgicalProcedure',
  bodyLocation: 'Jaw, Mouth',
  preparation: 'CBCT 3D scan, digital treatment planning, bone density assessment, health history review',
  followup: 'Healing abutment placement, final crown delivery, regular implant maintenance cleanings',
  howPerformed: 'Surgical placement of an FDA-approved titanium implant into the jawbone under local or IV sedation anesthesia, followed by osseointegration, abutment placement, and delivery of a custom porcelain crown fabricated in our in-house CAD/CAM laboratory.',
  expectedPrognosis: 'Suitability, outcomes and longevity depend on individual health, anatomy, treatment and ongoing maintenance. Implants and their restorations may need adjustments, repairs or replacement; no individual outcome or universal lifespan is guaranteed.',
  indication: { '@type': 'MedicalIndication', name: 'Missing teeth, tooth loss due to decay, trauma, or periodontal disease' },
  recognizingAuthority: { '@type': 'Organization', name: 'American Board of Oral Implantology' },
  usesDevice: [
    { '@type': 'MedicalDevice', name: 'CBCT 3D Cone Beam Scanner' },
    { '@type': 'MedicalDevice', name: 'FDA-Approved Titanium Implant System' },
    { '@type': 'MedicalDevice', name: 'CAD/CAM Milling Unit' },
  ],
  performer: DOC_REF,
  availableService: { '@id': ORG_ID },
};

// ─────────────────────────────────────────────
// ALL-ON-X / FULL ARCH
// ─────────────────────────────────────────────
export const allOnXProcedureSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/all-on-x#procedure`,
  name: 'All-on-X Full-Arch Dental Implants',
  alternateName: ['All-on-4', 'All-on-6', 'Fixed Full-Arch Implants', 'Same-Day Teeth', 'Teeth in a Day', 'Hybrid Prosthesis'],
  url: `${SITE_ORIGIN}/all-on-x`,
  description: 'All-on-X is a fixed full-arch dental restoration supported by four or more dental implants placed in the upper or lower jaw. It replaces a complete arch of missing or failing teeth with a non-removable prosthesis in a single surgical visit.',
  procedureType: 'https://schema.org/SurgicalProcedure',
  bodyLocation: 'Upper or lower dental arch (maxilla or mandible)',
  preparation: 'Comprehensive examination including CBCT 3D imaging, medical and dental history review, and digital treatment planning.',
  howPerformed: 'Under local anesthesia and optional IV sedation, four to six dental implants are surgically placed in the patient\'s jawbone. A fixed temporary prosthesis is typically attached the same day. After osseointegration (3–6 months), a permanent prosthesis is fabricated and seated.',
  followup: 'Post-operative checks at 1 week, 1 month, 3 months, and 6 months. Long-term hygiene visits every 6 months.',
  expectedPrognosis: 'Full-arch treatment suitability, outcomes and longevity depend on individual health, anatomy, treatment planning, healing and maintenance. The restoration and its components may need adjustments, repairs or replacement; no individual outcome or universal lifespan is guaranteed.',
  indication: { '@type': 'MedicalIndication', name: 'Complete tooth loss or terminal dentition in one or both arches; patients seeking a fixed alternative to removable dentures.' },
  usesDevice: [
    { '@type': 'MedicalDevice', name: 'Digital Implant Surgical Guide' },
    { '@type': 'MedicalDevice', name: 'Nobel Biocare / Straumann Implant Systems' },
    { '@type': 'MedicalDevice', name: 'In-House Milling & 3D Printing Lab' },
  ],
  performer: DOC_REF,
  availableService: { '@id': ORG_ID },
};

// ─────────────────────────────────────────────
// COSMETIC DENTISTRY
// ─────────────────────────────────────────────
export const cosmeticDentistryProcedureSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/cosmetic-dentistry#procedure`,
  name: 'Cosmetic Dentistry',
  alternateName: ['Porcelain Veneers', 'Smile Makeover', 'Teeth Whitening', 'Dental Bonding', 'Smile Design'],
  bodyLocation: 'Teeth, Gums',
  preparation: 'Digital smile design consultation, photos, impressions or digital scan',
  howPerformed: 'Custom porcelain veneers or composite veneers bonded to the front surfaces of teeth after minimal preparation; whitening performed with professional-grade bleaching gel.',
  indication: { '@type': 'MedicalIndication', name: 'Discolored, chipped, misshapen, or gapped teeth; desire for aesthetic smile enhancement' },
  usesDevice: [
    { '@type': 'MedicalDevice', name: 'Digital Smile Design Software' },
    { '@type': 'MedicalDevice', name: 'In-House Porcelain Milling Unit' },
    { '@type': 'MedicalDevice', name: 'Professional Whitening System' },
  ],
  performer: DOC_REF,
  availableService: { '@id': ORG_ID },
};

// ─────────────────────────────────────────────
// INVISALIGN
// ─────────────────────────────────────────────
export const invisalignProcedureSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/invisalign#procedure`,
  name: 'Invisalign Clear Aligners',
  alternateName: ['Clear Aligners', 'Invisible Braces', 'Orthodontics', 'Teeth Straightening'],
  bodyLocation: 'Teeth',
  preparation: 'iTero digital scan, ClinCheck treatment simulation and discussion of written treatment fees',
  howPerformed: 'Series of custom-fabricated clear aligner trays worn 20–22 hours/day, changed every 1–2 weeks, with checkups every 6–8 weeks. Treatment duration typically 6–18 months.',
  indication: { '@type': 'MedicalIndication', name: 'Crowded, spaced, or misaligned teeth; mild to moderate malocclusion' },
  usesDevice: [{ '@type': 'MedicalDevice', name: 'iTero Digital Intraoral Scanner' }],
  performer: DOC_REF,
  availableService: { '@id': ORG_ID },
};

// ─────────────────────────────────────────────
// EMERGENCY DENTISTRY
// ─────────────────────────────────────────────
export const emergencyDentistryProcedureSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/emergency-dentistry#procedure`,
  name: 'Emergency Dentistry',
  alternateName: ['Urgent Dental Care', 'Emergency Tooth Extraction'],
  bodyLocation: 'Mouth, Teeth, Jaw',
  howPerformed: 'Call the Trinity office to discuss the concern and confirm availability before traveling. The next available visit and treatment depend on the situation.',
  indication: { '@type': 'MedicalIndication', name: 'Severe tooth pain, dental abscess, knocked-out tooth, broken or cracked tooth, lost filling or crown, soft tissue injury' },
  performer: DOC_REF,
  availableService: { '@id': ORG_ID },
};

// ─────────────────────────────────────────────
// GENERAL DENTISTRY
// ─────────────────────────────────────────────
export const generalDentistrySchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/general-dentistry#procedure`,
  name: 'General Dentistry',
  alternateName: ['Family Dentist', 'Preventive Dentistry', 'Routine Dental Care', 'Dental Cleaning', 'Checkup'],
  bodyLocation: 'Teeth, Gums, Mouth',
  howPerformed: 'Comprehensive oral exams, digital X-rays, professional cleanings, fillings, and preventive treatments delivered by Dr. Gjelaj and her dental hygiene team.',
  performer: DOC_REF,
  availableService: { '@id': ORG_ID },
};

// ─────────────────────────────────────────────
// FULL MOUTH RESTORATION
// ─────────────────────────────────────────────
export const fullMouthRestorationSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/full-mouth-restoration#procedure`,
  name: 'Full Mouth Reconstruction',
  alternateName: ['Full Mouth Restoration', 'Complete Dental Rehabilitation', 'Smile Restoration'],
  bodyLocation: 'Teeth, gums and bite',
  preparation: 'Comprehensive records including CBCT, photographs, impressions, occlusal analysis',
  howPerformed: 'Individual plans may include restoring suitable natural teeth, replacing missing teeth with crowns, bridges or implant-supported restorations, and other care based on the examination and bite assessment.',
  performer: DOC_REF,
};

// ─────────────────────────────────────────────
// SEDATION DENTISTRY
// ─────────────────────────────────────────────
export const sedationDentistrySchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/sedation-dentistry#procedure`,
  name: 'Sedation Dentistry',
  alternateName: ['IV Sedation', 'Sleep Dentistry', 'Conscious Sedation', 'Dental Anxiety Treatment'],
  bodyLocation: 'Systemic',
  howPerformed: 'IV sedation administered by a board-certified anesthesiologist on-site. Patients remain comfortable and pain-free during dental procedures. Options include nitrous oxide, oral sedation, and IV sedation.',
  indication: { '@type': 'MedicalIndication', name: 'Dental anxiety, phobia, complex procedures, gag reflex, special needs patients' },
  usesDevice: [{ '@type': 'MedicalDevice', name: 'IV Sedation Equipment with Board-Certified Anesthesiologist' }],
  performer: DOC_REF,
};

// ─────────────────────────────────────────────
// FINANCING / OFFER
// ─────────────────────────────────────────────
export const financingOfferSchema = {
  '@context': 'https://schema.org',
  '@type': 'FinancialProduct',
  '@id': `${SITE_ORIGIN}/financing-options#financing`,
  name: 'Dental Financing at Trinity Dental Arts',
  description: 'Financing through CareCredit, Cherry and Proceed is subject to credit approval. The lender sets the terms.',
  provider: PERFORMER,
  feesAndCommissionsSpecification: 'No application fee. 0% interest promotional periods available through CareCredit.',
  offers: [
    { '@type': 'Offer', name: 'CareCredit Financing', description: '0% interest for 6, 12, 18, or 24 months on qualifying purchases' },
    { '@type': 'Offer', name: 'In-House Payment Plans', description: 'Custom payment arrangements available — call for details' },
    { '@type': 'Offer', name: 'Lending Club', description: 'Extended payment terms for larger treatment plans' },
  ],
};

// ─────────────────────────────────────────────
// ABOUT / MeetTheDoctor
// ─────────────────────────────────────────────
export const aboutPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': `${SITE_ORIGIN}/about#page`,
  name: 'About Trinity Dental Arts',
  url: `${SITE_ORIGIN}/about`,
  description: 'Learn about Trinity Dental Arts, a doctor-owned implant and cosmetic dental practice led by Dr. Xhoana Gjelaj in Trinity, FL.',
  about: ORG_REF,
  author: DR_GJELAJ_PERSON,
};

// ─────────────────────────────────────────────
// SERVICES PAGE
// ─────────────────────────────────────────────
export const servicesPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${SITE_ORIGIN}/services#page`,
  name: 'Dental Services at Trinity Dental Arts',
  url: `${SITE_ORIGIN}/services`,
  description: 'Complete list of dental services at Trinity Dental Arts, Trinity FL — including implants, cosmetic, general, emergency, sedation, Invisalign, oral surgery, and more.',
  provider: PERFORMER,
  hasPart: [
    { '@type': 'WebPage', name: 'Dental Implants', url: `${SITE_ORIGIN}/dental-implants` },
    { '@type': 'WebPage', name: 'All-on-X', url: `${SITE_ORIGIN}/all-on-x` },
    { '@type': 'WebPage', name: 'Cosmetic Dentistry', url: `${SITE_ORIGIN}/cosmetic-dentistry` },
    { '@type': 'WebPage', name: 'Invisalign', url: `${SITE_ORIGIN}/invisalign` },
    { '@type': 'WebPage', name: 'General Dentistry', url: `${SITE_ORIGIN}/general-dentistry` },
    { '@type': 'WebPage', name: 'Emergency Dentistry', url: `${SITE_ORIGIN}/emergency-dentistry` },
    { '@type': 'WebPage', name: 'Sedation Dentistry', url: `${SITE_ORIGIN}/sedation-dentistry` },
    { '@type': 'WebPage', name: 'Oral Surgery', url: `${SITE_ORIGIN}/oral-surgery` },
    { '@type': 'WebPage', name: 'Laser Dentistry', url: `${SITE_ORIGIN}/laser-dentistry` },
    { '@type': 'WebPage', name: 'Periodontal Disease Treatment', url: `${SITE_ORIGIN}/periodontal-disease-treatment` },
    { '@type': 'WebPage', name: 'Bone Grafting', url: `${SITE_ORIGIN}/bone-grafting` },
    { '@type': 'WebPage', name: 'Sinus Grafts', url: `${SITE_ORIGIN}/sinus-grafts` },
    { '@type': 'WebPage', name: 'Same-Day Crowns', url: `${SITE_ORIGIN}/same-day-crowns` },
    { '@type': 'WebPage', name: 'Dental Bridges', url: `${SITE_ORIGIN}/dental-bridges` },
    { '@type': 'WebPage', name: 'Dentures', url: `${SITE_ORIGIN}/dentures` },
    { '@type': 'WebPage', name: 'Preventative Dentistry', url: `${SITE_ORIGIN}/preventative-dentistry` },
  ],
};

// ─────────────────────────────────────────────
// CONTACT PAGE
// ─────────────────────────────────────────────
export const contactPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': `${SITE_ORIGIN}/contact-us#page`,
  name: 'Contact Trinity Dental Arts',
  url: `${SITE_ORIGIN}/contact-us`,
  description: 'Contact Trinity Dental Arts in Trinity, FL. Call (727) 228-6846 or book online. Mon–Thu 9am–5pm.',
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+1-727-228-6846',
      contactType: 'customer service',
      availableLanguage: ['English', 'Albanian'],
      hoursAvailable: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'], opens: '09:00', closes: '17:00' },
    },
    {
      '@type': 'ContactPoint',
      telephone: '+1-727-607-8699',
      contactType: 'SMS',
    },
  ],
  mainEntity: ORG_REF,
};

// ─────────────────────────────────────────────
// BOOK APPOINTMENT
// ─────────────────────────────────────────────
export const bookAppointmentSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_ORIGIN}/book-appointment#page`,
  name: 'Book a Dental Appointment — Trinity Dental Arts',
  url: `${SITE_ORIGIN}/book-appointment`,
  description: 'Book dental appointments online at Trinity Dental Arts in Trinity, FL. Choose the $300 Signature Consultation in the scheduler, credited toward treatment begun within 30 days.',
  potentialAction: {
    '@type': 'ReserveAction',
    name: 'Book Dental Appointment',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://trinitydentalarts.com/book-appointment#online-booking',
      actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform'],
    },
    result: { '@type': 'Reservation', name: 'Dental Appointment' },
  },
};

// ─────────────────────────────────────────────
// BLOG
// ─────────────────────────────────────────────
export const blogSchema = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  '@id': `${SITE_ORIGIN}/blog#page`,
  name: 'Trinity Dental Arts Blog',
  url: `${SITE_ORIGIN}/blog`,
  description: 'Expert articles on dental implants, cosmetic dentistry, oral health, and patient education from Dr. Xhoana Gjelaj and the Trinity Dental Arts team.',
  author: DR_GJELAJ_PERSON,
  publisher: PERFORMER,
  inLanguage: 'en-US',
  about: [
    { '@type': 'MedicalProcedure', name: 'Dental Implants' },
    { '@type': 'MedicalProcedure', name: 'Cosmetic Dentistry' },
    { '@type': 'MedicalProcedure', name: 'All-on-4' },
  ],
};

// ─────────────────────────────────────────────
// MEET THE DOCTOR
// ─────────────────────────────────────────────
export const meetTheDoctorSchema = {
  '@context': 'https://schema.org',
  '@type': ['ProfilePage', 'WebPage'],
  '@id': `${SITE_ORIGIN}/meet-the-doctor#page`,
  name: 'Meet Dr. Xhoana Gjelaj — Trinity Dental Arts',
  url: `${SITE_ORIGIN}/meet-the-doctor`,
  description: 'Dr. Xhoana Gjelaj, DMD, practices in Trinity, FL. Her professional credentials include MAGD Mastership, DABOI/ID and DICOI diplomate designations, and FAAID Fellowship.',
  mainEntity: DR_GJELAJ_PERSON,
};

// ─────────────────────────────────────────────
// OUR TECHNOLOGY
// ─────────────────────────────────────────────
export const ourTechnologySchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_ORIGIN}/our-technology#page`,
  name: 'Advanced Dental Technology — Trinity Dental Arts',
  url: `${SITE_ORIGIN}/our-technology`,
  description: 'Trinity Dental Arts uses CBCT 3D imaging, CAD/CAM same-day crowns, digital smile design, 3D printing, and laser dentistry for precise, comfortable, and efficient care.',
  about: [
    { '@type': 'MedicalDevice', name: 'CBCT 3D Cone Beam Scanner', description: 'Sub-millimeter 3D imaging for implant planning and diagnosis' },
    { '@type': 'MedicalDevice', name: 'CEREC CAD/CAM Milling Unit', description: 'In-house same-day crown fabrication' },
    { '@type': 'MedicalDevice', name: 'iTero Intraoral Scanner', description: 'Digital impressions — no goopy traditional impressions' },
    { '@type': 'MedicalDevice', name: '3D Dental Printer', description: 'In-house surgical guides, models, and provisionals' },
    { '@type': 'MedicalDevice', name: 'Dental Laser', description: 'Minimally invasive soft and hard tissue procedures' },
    { '@type': 'MedicalDevice', name: 'Digital Smile Design Software', description: 'Preview your new smile before treatment begins' },
  ],
};

// ─────────────────────────────────────────────
// OUR RESULTS (smile gallery)
// ─────────────────────────────────────────────
export const ourResultsSchema = {
  '@context': 'https://schema.org',
  '@type': 'ImageGallery',
  '@id': `${SITE_ORIGIN}/our-results#gallery`,
  name: 'Before & After Smile Gallery — Trinity Dental Arts',
  url: `${SITE_ORIGIN}/our-results`,
  description: 'Real patient before and after photos showing results from dental implants, All-on-4, veneers, and smile makeovers at Trinity Dental Arts in Trinity, FL.',
  author: DR_GJELAJ_PERSON,
};

// ─────────────────────────────────────────────
// FAQ PAGE
// ─────────────────────────────────────────────
export const faqPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${SITE_ORIGIN}/faq#page`,
  name: 'Frequently Asked Questions — Trinity Dental Arts',
  url: `${SITE_ORIGIN}/faq`,
  description: 'Common patient questions about dental implants, cosmetic dentistry, financing, and visiting Trinity Dental Arts in Trinity, FL.',
};

// ─────────────────────────────────────────────
// SPECIAL OFFER  (matches schemas-reference.md §6)
// ─────────────────────────────────────────────
export const specialOfferSchema = {
  '@context': 'https://schema.org',
  '@type': 'Offer',
  '@id': `${SITE_ORIGIN}/consult#signature-consultation`,
  name: '$300 Signature Consultation',
  description: 'A 90-minute in-office visit with Dr. Gjelaj, including an exam, CBCT, X-rays, an intraoral scan and a written treatment plan with exact pricing. The $300 fee is credited toward treatment begun within 30 days.',
  price: '300',
  priceCurrency: 'USD',
  url: `${SITE_ORIGIN}/consult`,
  offeredBy: ORG_REF,
  category: 'Dental Consultation',
  availability: 'https://schema.org/InStock',
  areaServed: [
    { '@type': 'City', name: 'Trinity, FL' },
    { '@type': 'City', name: 'Palm Harbor, FL' },
    { '@type': 'City', name: 'New Port Richey, FL' },
    { '@type': 'City', name: 'Tampa, FL' },
  ],
};

// ─────────────────────────────────────────────
// SPECIFIC IMPLANT SUB-PAGES
// ─────────────────────────────────────────────
export const singleImplantSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/single-implant#procedure`,
  name: 'Single Tooth Dental Implant',
  alternateName: ['Single Implant', 'One-Tooth Implant', 'Implant Crown'],
  procedureType: 'https://schema.org/SurgicalProcedure',
  bodyLocation: 'Individual Tooth Socket',
  howPerformed: 'A single titanium implant post is placed into the extraction socket or healed site. After osseointegration (3–6 months), a custom abutment and porcelain crown are attached — no modification to adjacent teeth.',
  performer: DOC_REF,
};

export const boneGraftingSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/bone-grafting#procedure`,
  name: 'Dental Bone Grafting',
  alternateName: ['Bone Augmentation', 'Ridge Augmentation', 'Bone Regeneration'],
  procedureType: 'https://schema.org/SurgicalProcedure',
  bodyLocation: 'Jawbone',
  howPerformed: 'Bone graft material (autograft, allograft, or synthetic) placed at extraction sites or areas of bone loss to regenerate sufficient bone volume for implant placement.',
  indication: { '@type': 'MedicalIndication', name: 'Insufficient jawbone volume for dental implant placement due to tooth loss, infection, or trauma' },
  performer: DOC_REF,
};

export const sinusGraftSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/sinus-grafts#procedure`,
  name: 'Sinus Lift / Sinus Graft',
  alternateName: ['Maxillary Sinus Augmentation', 'Sinus Augmentation'],
  procedureType: 'https://schema.org/SurgicalProcedure',
  bodyLocation: 'Maxillary Sinus, Upper Jaw',
  howPerformed: 'Bone graft material is added beneath the sinus membrane to increase bone height in the upper posterior jaw, enabling implant placement where natural bone is insufficient.',
  performer: DOC_REF,
};

export const sameDayCrownsSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/same-day-crowns#procedure`,
  name: 'Same-Day Dental Crowns (CEREC)',
  alternateName: ['CEREC Crowns', 'One-Visit Crowns', 'In-Office Crowns', 'CAD/CAM Crowns'],
  bodyLocation: 'Tooth',
  howPerformed: 'Digital intraoral scan is taken, crown is designed using CAD/CAM software, and milled from a ceramic block in our in-house lab — all in a single appointment. No temporary crown required.',
  usesDevice: [{ '@type': 'MedicalDevice', name: 'CEREC CAD/CAM Milling Unit' }],
  performer: DOC_REF,
};

export const periodontalSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/periodontal-disease-treatment#procedure`,
  name: 'Periodontal Disease Treatment',
  alternateName: ['Gum Disease Treatment', 'Scaling and Root Planing', 'Deep Cleaning', 'Gum Therapy'],
  bodyLocation: 'Gums, Periodontal Tissues',
  howPerformed: 'Scaling and root planing (deep cleaning) to remove tartar and bacteria below the gumline. Laser-assisted periodontal therapy available for advanced cases.',
  indication: { '@type': 'MedicalIndication', name: 'Gingivitis, periodontitis, gum recession, bone loss around teeth' },
  performer: DOC_REF,
};

export const oralSurgerySchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/oral-surgery#procedure`,
  name: 'Oral Surgery',
  alternateName: ['Tooth Extraction', 'Wisdom Tooth Removal', 'Surgical Extraction', 'Dental Surgery'],
  procedureType: 'https://schema.org/SurgicalProcedure',
  bodyLocation: 'Mouth, Jaw, Teeth',
  howPerformed: 'Surgical or non-surgical tooth extractions, wisdom tooth removal, and other minor oral surgical procedures performed under local or IV sedation anesthesia.',
  performer: DOC_REF,
};

export const laserDentistrySchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/laser-dentistry#procedure`,
  name: 'Laser Dentistry',
  alternateName: ['Dental Laser Treatment', 'Soft Tissue Laser', 'LANAP'],
  bodyLocation: 'Gums, Soft Tissue, Teeth',
  howPerformed: 'Dental laser used for gum contouring, frenectomy, cavity preparation, periodontal therapy, and teeth whitening activation — less invasive than traditional methods with faster healing.',
  usesDevice: [{ '@type': 'MedicalDevice', name: 'Dental Diode Laser' }],
  performer: DOC_REF,
};

export const dentalBridgesSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/dental-bridges#procedure`,
  name: 'Dental Bridges',
  alternateName: ['Fixed Bridge', 'Tooth Bridge', 'Implant-Supported Bridge'],
  bodyLocation: 'Teeth',
  howPerformed: 'A fixed dental bridge spans one or more missing teeth, anchored to natural teeth or implants on either side. Traditional or implant-supported options available.',
  performer: DOC_REF,
};

export const denturesSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_ORIGIN}/dentures#webpage`,
  url: `${SITE_ORIGIN}/dentures`,
  name: 'Fixed Alternatives to Dentures in Trinity, FL',
  description: 'Compare removable dentures with fixed implant teeth. Trinity Dental Arts offers fixed implant restorations and does not offer removable snap-in dentures.',
  about: ORG_REF,
};

export const regenerativeDentistrySchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  '@id': `${SITE_ORIGIN}/regenerative-dentistry#procedure`,
  name: 'Regenerative Dentistry',
  alternateName: ['Guided Bone Regeneration', 'Guided Tissue Regeneration', 'PRF Therapy'],
  procedureType: 'https://schema.org/SurgicalProcedure',
  bodyLocation: 'Jaw Bone, Gum Tissue',
  howPerformed: 'Use of membranes, bone graft materials, and biologics (e.g., PRF — Platelet Rich Fibrin) to stimulate regrowth of lost bone and tissue, supporting implant longevity.',
  performer: DOC_REF,
};

// ─────────────────────────────────────────────
// SHARED UTILITY FUNCTIONS
// ─────────────────────────────────────────────

/**
 * Generate an Article schema for blog posts.
 * @param {Object} post - BlogPost entity record
 */
export function articleSchema(post) {
  const slug = post.slug || post.id;
  const image = post.featured_image || `${SITE_ORIGIN}/og-default.jpg`;
  const published = post.created_date || new Date().toISOString();
  const modified = post.updated_date || published;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${SITE_ORIGIN}/blog/${slug}#article`,
    headline: post.title,
    description: post.meta_description || post.excerpt || '',
    image,
    datePublished: published,
    dateModified: modified,
    author: DOC_REF,
    publisher: ORG_REF,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_ORIGIN}/blog/${slug}`,
    },
    articleSection: post.category || 'Dental Health',
    wordCount: post.content ? Math.round(post.content.split(/\s+/).length) : undefined,
    url: `${SITE_ORIGIN}/blog/${slug}`,
  };
}

export function breadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${SITE_ORIGIN}${item.path}`,
    })),
  };
}

export function faqSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: { '@type': 'Answer', text: q.answer },
    })),
  };
}

export function combineSchemas(...schemas) {
  return {
    '@context': 'https://schema.org',
    '@graph': schemas.filter(Boolean).map(({ '@context': _ctx, ...rest }) => rest),
  };
}